import { AssetList, Chain } from '@chain-registry/types'
import { z } from 'zod'

import packageJson from '../package.json'
import { ChainInfo } from './libs/keplr/types'

const envSchema = z.object({
  VITE_APP_NAME: z.string(),

  VITE_ENVIRONMENT: z
    .enum(['development', 'staging', 'production'])
    .default('development'),

  VITE_IPFS_GATEWAY_URL: z.string(),
  VITE_IPFS_API_URL: z.string(),

  VITE_BRIDGELESS_REST_API_URL: z.string(),
  VITE_BRIDGELESS_RPC_URL: z.string(),

  VITE_BRIDGELESS_SCAN_URL: z.string().optional().default(''),
})

export const config = {
  BUILD_VERSION: packageJson.version,
  MODE: import.meta.env.MODE,
  ...envSchema.parse(import.meta.env),

  DISCORD_LINK: 'https://discord.com/invite/cloneofficial',
  X_LINK: 'https://x.com/ConfidentialLyr',
  DOC_LINK: 'https://docs.confidentiallayer.com/',
  BRIDGE_APP_LINK: 'https://staging.bridge.confidentiallayer.com',
}

const CHAIN_IDS = {
  development: 'bridge_13441-1',
  staging: 'bridge_2607-1',
  production: 'bridge_13441-1',
}

const CHAIN_NAMES = {
  development: 'Bridgeless Testnet',
  staging: 'Bridgeless Staging',
  production: 'Bridgeless',
}

const currentChainId = CHAIN_IDS[config.VITE_ENVIRONMENT]
const currentChainName = CHAIN_NAMES[config.VITE_ENVIRONMENT]

if (!currentChainId)
  throw new Error(
    `Invalid chain ID for environment: ${config.VITE_ENVIRONMENT}`,
  )

if (!currentChainName)
  throw new Error(
    `Invalid chain name for environment: ${config.VITE_ENVIRONMENT}`,
  )

export const bridgelessChainRegistry: Chain[] = [
  {
    chain_name: 'bridgeless',
    chain_id: currentChainId,
    chain_type: 'cosmos',
    bech32_prefix: 'bridge',
    bech32_config: {
      bech32PrefixAccAddr: 'bridge',
      bech32PrefixAccPub: 'bridgepub',
      bech32PrefixValAddr: 'bridgevaloper',
      bech32PrefixValPub: 'bridgevaloperpub',
      bech32PrefixConsAddr: 'bridgevalcons',
      bech32PrefixConsPub: 'bridgevalconspub',
    },
    key_algos: ['ethsecp256k1'],
    slip44: 60,
    apis: {
      rpc: [{ address: config.VITE_BRIDGELESS_RPC_URL }],
      rest: [{ address: config.VITE_BRIDGELESS_REST_API_URL }],
    },
    logo_URIs: { png: '/logos/bridgeless.png', svg: '/logos/bridgeless.svg' },
  },
]

export const bridgelessAssetList: AssetList[] = [
  {
    chain_name: 'bridgeless',
    assets: [
      {
        base: 'abridge',
        name: 'Bridge',
        display: 'BRIDGE',
        symbol: 'BRIDGE',
        type_asset: 'sdk.coin',
        denom_units: [
          { denom: 'abridge', exponent: 0 },
          { denom: 'BRIDGE', exponent: 18 },
        ],
        logo_URIs: {
          png: '/logos/bridgeless.png',
          svg: '/logos/bridgeless.svg',
        },
      },
    ],
  },
] as const

export const bridgelessChainInfo: ChainInfo = {
  chainId: currentChainId,
  chainName: currentChainName,
  rpc: config.VITE_BRIDGELESS_RPC_URL,
  rest: config.VITE_BRIDGELESS_REST_API_URL,
  bip44: { coinType: 60 },
  bech32Config: {
    bech32PrefixAccAddr: 'bridge',
    bech32PrefixAccPub: 'bridgepub',
    bech32PrefixValAddr: 'bridgevaloper',
    bech32PrefixValPub: 'bridgevaloperpub',
    bech32PrefixConsAddr: 'bridgevalcons',
    bech32PrefixConsPub: 'bridgevalconspub',
  },
  stakeCurrency: {
    coinDenom: 'BRIDGE',
    coinMinimalDenom: 'abridge',
    coinDecimals: 18,
  },
  currencies: [
    { coinDenom: 'BRIDGE', coinMinimalDenom: 'abridge', coinDecimals: 18 },
  ],
  feeCurrencies: [
    {
      coinDenom: 'BRIDGE',
      coinMinimalDenom: 'abridge',
      coinDecimals: 18,
      gasPriceStep: {
        low: 1_000_000_000,
        average: 1_000_000_000,
        high: 1_000_000_000,
      },
    },
  ],
  features: ['eth-address-gen', 'eth-key-sign'],
}

// cosmos-kit chain name key used by useChain(CHAIN_NAME)
export const CHAIN_NAME = 'bridgeless'
